import React from 'react';
import { FeeInputs } from '../components/FeeInputs';
import { Loader2 } from 'lucide-react';
import { Merchant, MerchantFee } from '../../../types';

interface FeeStructureStepProps {
  merchants: Merchant[];
  merchantFees: MerchantFee[];
  loading: boolean;
  fees: {
    merchantId: string;
    useCustomFees: boolean;
    payments: Record<string, {
      fixedFee: number | '';
      percentageFee: number | '';
      maxCap: number | '';
    }>;
    payouts: Record<string, {
      fixedFee: number | '';
      percentageFee: number | '';
      maxCap: number | '';
    }>;
  };
  onMerchantChange: (merchantId: string) => void;
  onFeesChange: (fees: typeof FeeStructureStepProps['fees']) => void;
}

export function FeeStructureStep({
  merchants,
  merchantFees,
  loading,
  fees,
  onMerchantChange,
  onFeesChange
}: FeeStructureStepProps) {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Convert empty string values to 0 before submitting
    const normalizedFees = {
      ...fees,
      payments: Object.fromEntries(
        Object.entries(fees.payments).map(([status, values]) => [
          status,
          {
            fixedFee: values.fixedFee === '' ? 0 : values.fixedFee,
            percentageFee: values.percentageFee === '' ? 0 : values.percentageFee,
            maxCap: values.maxCap === '' ? 0 : values.maxCap
          }
        ])
      ),
      payouts: Object.fromEntries(
        Object.entries(fees.payouts).map(([status, values]) => [
          status,
          {
            fixedFee: values.fixedFee === '' ? 0 : values.fixedFee,
            percentageFee: values.percentageFee === '' ? 0 : values.percentageFee,
            maxCap: values.maxCap === '' ? 0 : values.maxCap
          }
        ])
      )
    };
  }

  return (
    <div className="space-y-8">
      <div>
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        )}
        <h3 className="text-lg font-medium text-gray-900 mb-4">Fee Structure</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Fee Structure
            </label>
            <select
              value={fees.merchantId || ''}
              onChange={(e) => onMerchantChange(e.target.value)}
              className="mt-1 block w-full"
            >
              <option value="">Custom Fee Structure</option>
              {merchants.map((merchant) => (
                <option key={merchant.id} value={merchant.id}>
                  {merchant.name}
                </option>
              ))}
            </select>
          </div>

          {fees.merchantId && (
            <div className="text-sm text-gray-500">
              <p>
                Using fee structure from selected merchant. Switch to custom fees to make changes.
              </p>
            </div>
          )}

          {/* Fee Structure Forms */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Payment Fees */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-4">
                Payment Fees - eTransfer Request Money
              </h4>
              <FeeInputs
                fees={fees.payments}
                disabled={!!fees.merchantId}
                onChange={(newFees) => onFeesChange({
                  ...fees,
                  payments: newFees
                })}
              />
            </div>

            {/* Payout Fees */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-4">
                Payout Fees - eTransfer Send Money
              </h4>
              <FeeInputs
                fees={fees.payouts}
                disabled={!!fees.merchantId}
                onChange={(newFees) => onFeesChange({
                  ...fees,
                  payouts: newFees
                })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}